import _ from 'underscore';

import {ProcessesFeature, RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ReviewSummary} from 'constants/SourceConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isAgentTypeEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import licenses from 'user/Licenses';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';

import {MyWorkCategory} from './my-work-v2/MyWorkConstants';

export function hasReviewsLicense(): boolean {
  return licenses.hasLicense('LICENSE_REVIEWS');
}

export function hasReviewSummaryData(reviewSummary: ReviewSummary | null): boolean {
  return reviewSummary != null && !_.isEmpty(reviewSummary);
}

/** Matches {@link MyWorkSection} template review summary section visibility. */
export function shouldShowTemplateReviews(): boolean {
  return hasReviewsLicense() && BenchlingConfig.ELN_ENABLE_TEMPLATE_REVIEWS;
}

export function shouldShowAssignedProcessTasks(): boolean {
  return (
    isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT}) &&
    isModelTypeEnabledForListing(ModelTypeConstants.PROCESS_TASK)
  );
}

export function shouldShowWorklistsSection(): boolean {
  return (
    BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS &&
    isFeatureEnabled({feature: RegistryFeature.REGISTRY_WORKLISTS})
  );
}

export function shouldShowAdminBanner(): boolean {
  return (
    BenchlingConfig.NAV_HOMEPAGE_ADMIN_BANNER != null && BenchlingConfig.NAV_HOMEPAGE_ADMIN_BANNER !== ''
  );
}

export function shouldShowAISection(): boolean {
  return isAgentTypeEnabled(AgentType.ASSISTANT);
}

export function shouldShowWelcomeSection(): boolean {
  return BenchlingConfig.ENABLE_NUE_MODULES;
}

export function getEnabledMyWorkCategories({
  entryReviewSummary = null,
  worksheetReviewSummary = null,
  templateReviewSummary = null,
}: {
  entryReviewSummary?: ReviewSummary | null;
  worksheetReviewSummary?: ReviewSummary | null;
  templateReviewSummary?: ReviewSummary | null;
} = {}): Array<MyWorkCategory> {
  const categories: Array<MyWorkCategory> = [];
  if (hasReviewsLicense() && hasReviewSummaryData(entryReviewSummary)) {
    categories.push(MyWorkCategory.ENTRIES);
  }
  if (hasReviewsLicense() && hasReviewSummaryData(worksheetReviewSummary)) {
    categories.push(MyWorkCategory.WORKSHEETS);
  }
  if (shouldShowTemplateReviews() && hasReviewSummaryData(templateReviewSummary)) {
    categories.push(MyWorkCategory.TEMPLATES);
  }
  if (shouldShowAssignedProcessTasks()) {
    categories.push(MyWorkCategory.TASKS);
  }
  return categories;
}

/** Coarse gate for rendering {@link MyWorkSection} in {@link Homepage}. */
export function shouldShowMyWorkSection(): boolean {
  return hasReviewsLicense() || shouldShowTemplateReviews() || shouldShowAssignedProcessTasks();
}

export function shouldShowWhatsNewButton(): boolean {
  return !BenchlingConfig.VALIDATED_CLOUD_VERSION;
}
